/**
 * Color theming for per-project Gantt / schedule colors.
 *
 * Canonical preset table is duplicated on the backend. If a project's
 * `color_scheme` field is null/missing, fall back to `epic_plan`.
 */

export const COLOR_PRESETS = {
  primavera: {
    critical: '#DC2626',
    completed: '#2563EB',
    in_progress: '#16A34A',
    not_started: '#94A3B8',
  },
  epic_plan: {
    critical: '#EA580C',
    completed: '#059669',
    in_progress: '#FACC15',
    not_started: '#94A3B8',
  },
};

export const DEFAULT_PRESET = 'epic_plan';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const isValidHex = (v) => typeof v === 'string' && HEX_RE.test(v);

const isValidCustomColors = (c) =>
  c &&
  typeof c === 'object' &&
  isValidHex(c.critical) &&
  isValidHex(c.completed) &&
  isValidHex(c.in_progress) &&
  isValidHex(c.not_started);

/**
 * Resolve a project's color_scheme field to a concrete {critical, completed,
 * in_progress, not_started} hex table. Handles null, malformed custom, and
 * unknown preset names by falling back to the default.
 */
export const resolveColors = (colorScheme) => {
  if (!colorScheme || typeof colorScheme !== 'object') {
    return COLOR_PRESETS[DEFAULT_PRESET];
  }

  const { preset, custom_colors } = colorScheme;

  if (preset === 'custom') {
    return isValidCustomColors(custom_colors)
      ? { ...custom_colors }
      : COLOR_PRESETS[DEFAULT_PRESET];
  }

  if (preset && COLOR_PRESETS[preset]) {
    return COLOR_PRESETS[preset];
  }

  return COLOR_PRESETS[DEFAULT_PRESET];
};

/**
 * Pure replacement for the legacy `getBarColor` closure in GanttTimeline.
 * Status precedence: completed > in_progress > critical > not_started.
 * Returns a hex string from the provided colors table.
 */
export const getBarColorForActivity = (activity, colors) => {
  if (!activity) return colors.not_started;

  // Completed: explicit status, actual_finish present, or percent_complete >= 100
  const isCompleted =
    activity.status === 'completed' ||
    activity.actual_finish ||
    (activity.percent_complete && activity.percent_complete >= 100);

  if (isCompleted) return colors.completed;

  // In progress: explicit status, actual_start present, or percent_complete > 0
  const isInProgress =
    activity.status === 'in_progress' ||
    activity.actual_start ||
    (activity.percent_complete && activity.percent_complete > 0);

  if (isInProgress) return colors.in_progress;

  // Critical path activities (not started yet)
  if (activity.is_critical || activity.total_float === 0) {
    return colors.critical;
  }

  return colors.not_started;
};
